import type { UserProfile } from '../api/types'
import type { StoredMessage } from '../storage/db'

/** Suggests a valid username (2–32 of a-z 0-9 _ . -, alnum first) from a display name. */
export function suggestUsername(displayName: string): string {
  const base = displayName
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9_.-]+/g, '')
    .replace(/^[^a-z0-9]+/, '')
    .slice(0, 32)
  if (base.length >= 2) return base
  return (base + 'friend').slice(0, 32)
}

export function isValidUsername(u: string): boolean {
  return /^[a-z0-9][a-z0-9_.-]{1,31}$/.test(u)
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? '?'
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : (parts[0]?.[1] ?? '')
  return (first + second).toUpperCase()
}

/** Stable hue (0–359) for a user's avatar color. */
export function hueFor(id: string): number {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  return h % 360
}

export function displayName(profiles: Map<string, UserProfile>, userId: string): string {
  return profiles.get(userId)?.display_name ?? userId.split('@')[0] ?? userId
}

/**
 * A DM's name: the group's own name if it has one, else the others in it ("Bob, Carol, Dan"
 * or "Bob, Carol +3"), or "Just you" once they've left.
 */
export function dmTitle(
  profiles: Map<string, UserProfile>,
  members: readonly string[],
  me: string | null,
  name?: string | null,
): string {
  if (name) return name
  const others = members.filter((m) => m !== me).map((m) => displayName(profiles, m))
  if (others.length === 0) return 'Just you'
  return others.length <= 3 ? others.join(', ') : `${others.slice(0, 2).join(', ')} +${others.length - 2}`
}

export function formatTime(ms: number, now = Date.now()): string {
  const d = new Date(ms)
  const sameDay = new Date(now).toDateString() === d.toDateString()
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  if (sameDay) return time
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`
}

export interface MessageGroup {
  sender: string
  firstTs: number
  messages: StoredMessage[]
}

const GROUP_WINDOW_MS = 5 * 60 * 1000

export type TimelineItem =
  | ({ kind: 'group' } & MessageGroup)
  | { kind: 'notice'; msg: StoredMessage }
  | { kind: 'party'; msg: StoredMessage }

/** Replies by the message they answer, in arrival order. */
export interface ReplyTree {
  children: Map<string, StoredMessage[]>
  /** Replies shown in a tree rather than in the timeline. */
  threaded: Set<string>
  /** Each of those replies' thread: the top-level message it all hangs from. */
  roots: Map<string, string>
}

/** A top-level message with replies, for the Threads list. */
export interface ThreadSummary {
  root: StoredMessage
  replies: number
  last: StoredMessage
}

/**
 * The reply tree: a reply hangs under the message it answers when that is loaded and
 * came before it (so a forged reply_to can't make a loop); otherwise it stays in the timeline.
 */
export function replyTree(msgs: StoredMessage[]): ReplyTree {
  const byId = new Map(msgs.map((m) => [m.envelope.id, m]))
  const children = new Map<string, StoredMessage[]>()
  const threaded = new Set<string>()
  const roots = new Map<string, string>()
  for (const m of msgs) {
    if (m.envelope.type !== 'text' || !m.envelope.reply_to) continue
    const parent = byId.get(m.envelope.reply_to)
    if (!parent || parent.seq >= m.seq) continue
    const list = children.get(parent.envelope.id) ?? []
    list.push(m)
    children.set(parent.envelope.id, list)
    threaded.add(m.envelope.id)
    // Messages are in seq order, so the parent's thread is known already.
    roots.set(m.envelope.id, roots.get(parent.envelope.id) ?? parent.envelope.id)
  }
  return { children, threaded, roots }
}

/** Threads in a conversation, latest reply first. */
export function threadSummaries(msgs: StoredMessage[], tree: ReplyTree): ThreadSummary[] {
  const byId = new Map(msgs.map((m) => [m.envelope.id, m]))
  const out = new Map<string, ThreadSummary>()
  for (const m of msgs) {
    const rootId = tree.roots.get(m.envelope.id)
    const root = rootId ? byId.get(rootId) : undefined
    if (!root) continue
    const t = out.get(root.envelope.id)
    if (!t) out.set(root.envelope.id, { root, replies: 1, last: m })
    else {
      t.replies++
      if (m.seq > t.last.seq) t.last = m
    }
  }
  return [...out.values()].sort((a, b) => b.last.seq - a.last.seq)
}

/** How many replies sit under a message, at any depth. */
export function replyCount(children: Map<string, StoredMessage[]>, id: string): number {
  return (children.get(id) ?? []).reduce((n, r) => n + 1 + replyCount(children, r.envelope.id), 0)
}

/**
 * Timeline rows: consecutive text messages by the same sender within a few minutes are
 * grouped; membership notices stand alone and break groups. Messages in `threaded` show
 * under their thread's original instead.
 */
export function timelineItems(msgs: StoredMessage[], threaded: ReadonlySet<string> = new Set()): TimelineItem[] {
  const items: TimelineItem[] = []
  for (const m of msgs) {
    if (threaded.has(m.envelope.id)) continue
    if (m.envelope.type === 'system') {
      items.push({ kind: 'notice', msg: m })
      continue
    }
    if (m.envelope.type === 'conv_name') {
      // Re-telling newcomers the name isn't news.
      if (!m.envelope.repeat) items.push({ kind: 'notice', msg: m })
      continue
    }
    if (m.envelope.type === 'party') {
      // Only the start shows; control events are invisible plumbing.
      if (m.envelope.party.action === 'start') items.push({ kind: 'party', msg: m })
      continue
    }
    if (!['text', 'media', 'library'].includes(m.envelope.type)) continue
    const ts = m.envelope.ts || m.receivedAt
    const last = items[items.length - 1]
    const lastMsg = last?.kind === 'group' ? last.messages[last.messages.length - 1] : undefined
    const lastTs = lastMsg ? lastMsg.envelope.ts || lastMsg.receivedAt : 0
    if (last?.kind === 'group' && last.sender === m.sender.user_id && ts - lastTs < GROUP_WINDOW_MS) {
      last.messages.push(m)
    } else {
      items.push({ kind: 'group', sender: m.sender.user_id, firstTs: ts, messages: [m] })
    }
  }
  return items
}

/** Human text for a membership notice. */
export function noticeText(event: string, name: string): string {
  switch (event) {
    case 'joined':
      return `${name} joined`
    case 'left':
      return `${name} left`
    case 'device_added':
      return `${name} added a new device — their safety number changed`
    case 'device_removed':
      return `${name} removed a device — their safety number changed`
    case 'missed':
      return 'Messages sent while this device was away are gone: the server keeps them 30 days at most'
    default:
      return `${name} changed membership`
  }
}

/** Merges new messages into a sorted list, de-duplicating by seq. */
export function mergeMessages(existing: StoredMessage[], incoming: StoredMessage[]): StoredMessage[] {
  const bySeq = new Map<number, StoredMessage>()
  for (const m of existing) bySeq.set(m.seq, m)
  for (const m of incoming) bySeq.set(m.seq, m)
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq)
}

/** Formats a safety code as groups of 4 hex digits (first 32). */
export function formatSafetyCode(hex: string): string {
  return (hex.slice(0, 32).match(/.{1,4}/g) ?? []).join(' ')
}

/** How long until `until`: "12 min", "3 h", "2 days" (to the nearest; at least a minute). */
export function timeLeft(until: number, now = Date.now()): string {
  const min = Math.max(1, Math.round((until - now) / 60_000))
  if (min < 60) return `${min} min`
  const h = Math.ceil(min / 60)
  if (h < 48) return `${h} h`
  return `${Math.ceil(h / 24)} days`
}
